import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import Chart from '../../components/ui/Chart';
import {
  BarChart3, TrendingUp, TrendingDown, Star, Users, MessageSquare,
  ThumbsUp, ThumbsDown, Smile, Frown, Meh, Target, Award, Trophy,
  Calendar, Clock, Filter, Search, Download, RefreshCw, Eye,
  AlertCircle, CheckCircle, Info, HelpCircle, ExternalLink, Copy,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, MoreHorizontal, MoreVertical, Settings,
  Edit, Trash2, Plus, Minus, Sort, Home, Menu, X, Check, XCircle,
  CheckCircle2, Lock, Unlock, Key, Bell, Mail, Phone, MessageCircle,
  Heart, Zap, Shield, Crown, Medal, Gift, Coffee, Pizza, Cake,
  Music, Camera, Video, File, Upload, Share, Bookmark, Flag,
  Activity, User, UserCheck, UserPlus, UserMinus, Database, Monitor,
  Cpu, HardDrive, Network, Wifi, Signal, Battery, Volume, Volume2,
  VolumeX, Play, Pause, Stop, SkipBack, SkipForward, RotateCcw,
  RotateCw, Maximize, Minimize, Maximize2, Minimize2, Square,
  Circle, Triangle, Hexagon, Octagon, Pentagon, Diamond, Star as StarIcon,
  PieChart as PieChartIcon
} from 'lucide-react';

const FeedbackAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Mock analytics data
    const mockAnalytics = {
      overview: {
        totalFeedback: 1247,
        averageRating: 4.2,
        satisfactionRate: 87.5,
        responseRate: 73.2,
        trend: '+12.3%',
        trendDirection: 'up'
      },
      satisfactionTrends: [
        { date: '2025-01-01', rating: 4.1, count: 45 },
        { date: '2025-01-02', rating: 4.3, count: 52 },
        { date: '2025-01-03', rating: 4.0, count: 38 },
        { date: '2025-01-04', rating: 4.4, count: 61 },
        { date: '2025-01-05', rating: 4.2, count: 55 },
        { date: '2025-01-06', rating: 4.5, count: 48 },
        { date: '2025-01-07', rating: 4.3, count: 67 },
        { date: '2025-01-08', rating: 4.1, count: 42 },
        { date: '2025-01-09', rating: 4.6, count: 73 },
        { date: '2025-01-10', rating: 4.4, count: 58 },
        { date: '2025-01-11', rating: 4.2, count: 49 },
        { date: '2025-01-12', rating: 4.7, count: 81 },
        { date: '2025-01-13', rating: 4.3, count: 56 },
        { date: '2025-01-14', rating: 4.5, count: 64 },
        { date: '2025-01-15', rating: 4.4, count: 59 }
      ],
      agentPerformance: [
        {
          id: 1,
          name: 'Sarah Johnson',
          avatar: 'SJ',
          totalTickets: 156,
          averageRating: 4.6,
          satisfactionRate: 94.2,
          responseTime: '2.3h',
          feedbackCount: 89,
          trend: '+8.5%'
        },
        {
          id: 2,
          name: 'Mike Chen',
          avatar: 'MC',
          totalTickets: 142,
          averageRating: 4.4,
          satisfactionRate: 91.8,
          responseTime: '1.8h',
          feedbackCount: 76,
          trend: '+12.1%'
        },
        {
          id: 3,
          name: 'Emily Davis',
          avatar: 'ED',
          totalTickets: 134,
          averageRating: 4.2,
          satisfactionRate: 88.9,
          responseTime: '3.1h',
          feedbackCount: 68,
          trend: '+5.3%'
        },
        {
          id: 4,
          name: 'David Wilson',
          avatar: 'DW',
          totalTickets: 128,
          averageRating: 4.0,
          satisfactionRate: 85.4,
          responseTime: '2.7h',
          feedbackCount: 62,
          trend: '-2.1%'
        },
        {
          id: 5,
          name: 'Lisa Brown',
          avatar: 'LB',
          totalTickets: 119,
          averageRating: 4.3,
          satisfactionRate: 90.1,
          responseTime: '2.1h',
          feedbackCount: 58,
          trend: '+15.7%'
        }
      ],
      categoryBreakdown: [
        { category: 'Technical Support', count: 456, averageRating: 4.3, satisfactionRate: 89.2 },
        { category: 'Billing', count: 234, averageRating: 4.1, satisfactionRate: 86.7 },
        { category: 'Account Management', count: 189, averageRating: 4.5, satisfactionRate: 92.1 },
        { category: 'General Inquiry', count: 156, averageRating: 4.0, satisfactionRate: 84.3 },
        { category: 'Feature Request', count: 98, averageRating: 4.2, satisfactionRate: 87.8 },
        { category: 'Bug Report', count: 67, averageRating: 3.8, satisfactionRate: 81.2 },
        { category: 'Training', count: 47, averageRating: 4.6, satisfactionRate: 95.7 }
      ],
      ratingDistribution: [
        { rating: '5 Stars', count: 523, percentage: 42.0 },
        { rating: '4 Stars', count: 398, percentage: 31.9 },
        { rating: '3 Stars', count: 189, percentage: 15.2 },
        { rating: '2 Stars', count: 89, percentage: 7.1 },
        { rating: '1 Star', count: 48, percentage: 3.8 }
      ],
      improvementAreas: [
        { area: 'Response Time', mentions: 234, sentiment: 'negative', priority: 'high' },
        { area: 'Agent Knowledge', mentions: 189, sentiment: 'positive', priority: 'medium' },
        { area: 'Communication', mentions: 156, sentiment: 'mixed', priority: 'high' },
        { area: 'Resolution Quality', mentions: 134, sentiment: 'positive', priority: 'medium' },
        { area: 'Follow-up', mentions: 98, sentiment: 'negative', priority: 'high' },
        { area: 'System Performance', mentions: 87, sentiment: 'negative', priority: 'medium' },
        { area: 'Documentation', mentions: 76, sentiment: 'mixed', priority: 'low' }
      ],
      benchmarks: {
        industryAverage: 3.8,
        competitorAverage: 4.1,
        ourAverage: 4.2,
        industrySatisfaction: 78.5,
        ourSatisfaction: 87.5
      },
      recentFeedback: [
        {
          id: 1,
          customer: 'John Smith',
          agent: 'Sarah Johnson',
          rating: 5,
          category: 'Technical Support',
          comment: 'Excellent support! Sarah was very helpful and resolved my issue quickly.',
          date: '2025-01-15T14:30:00Z',
          sentiment: 'positive'
        },
        {
          id: 2,
          customer: 'Maria Garcia',
          agent: 'Mike Chen',
          rating: 4,
          category: 'Billing',
          comment: 'Good support overall, but took a bit longer than expected.',
          date: '2025-01-15T13:45:00Z',
          sentiment: 'positive'
        },
        {
          id: 3,
          customer: 'Robert Lee',
          agent: 'Emily Davis',
          rating: 3,
          category: 'General Inquiry',
          comment: 'Average experience. Could be better.',
          date: '2025-01-15T12:20:00Z',
          sentiment: 'neutral'
        },
        {
          id: 4,
          customer: 'Jennifer White',
          agent: 'David Wilson',
          rating: 2,
          category: 'Technical Support',
          comment: 'Poor communication and slow response time.',
          date: '2025-01-15T11:15:00Z',
          sentiment: 'negative'
        },
        {
          id: 5,
          customer: 'Michael Brown',
          agent: 'Lisa Brown',
          rating: 5,
          category: 'Account Management',
          comment: 'Outstanding service! Lisa went above and beyond.',
          date: '2025-01-15T10:30:00Z',
          sentiment: 'positive'
        }
      ]
    };

    setAnalytics(mockAnalytics);
  }, []);

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      case 'neutral': return 'text-yellow-600 bg-yellow-100';
      case 'mixed': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <Smile className="h-4 w-4" />;
      case 'negative': return <Frown className="h-4 w-4" />;
      case 'neutral': return <Meh className="h-4 w-4" />;
      case 'mixed': return <MessageSquare className="h-4 w-4" />;
      default: return <HelpCircle className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredRecentFeedback = analytics?.recentFeedback.filter(feedback => {
    const matchesSearch = feedback.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feedback.agent.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feedback.comment.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAgent = selectedAgent === 'all' || feedback.agent === selectedAgent;
    const matchesCategory = selectedCategory === 'all' || feedback.category === selectedCategory;
    
    return matchesSearch && matchesAgent && matchesCategory;
  }) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feedback Analytics</h1>
          <p className="text-gray-600 mt-2">Analyze customer feedback and satisfaction trends</p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Feedback</p>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview.totalFeedback.toLocaleString()}</p>
                <p className={`text-sm flex items-center gap-1 ${
                  analytics?.overview.trendDirection === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {analytics?.overview.trendDirection === 'up' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  {analytics?.overview.trend}
                </p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Rating</p>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview.averageRating}</p>
                <p className="text-sm text-gray-500">out of 5.0</p>
              </div>
              <Star className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Satisfaction Rate</p>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview.satisfactionRate}%</p>
                <p className="text-sm text-green-600">+8.9% vs industry</p>
              </div>
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Response Rate</p>
                <p className="text-2xl font-bold text-gray-900">{analytics?.overview.responseRate}%</p>
                <p className="text-sm text-blue-600">73.2% of customers</p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Satisfaction Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              type="line"
              data={analytics?.satisfactionTrends || []}
              xKey="date"
              yKey="rating"
              height={300}
              color="#3B82F6"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Rating Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              type="bar"
              data={analytics?.ratingDistribution || []}
              xKey="rating"
              yKey="count"
              height={300}
              color="#10B981"
            />
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Agent Performance Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics?.agentPerformance.map((agent) => (
              <div key={agent.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="font-semibold text-blue-600">{agent.avatar}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{agent.name}</h3>
                      <p className="text-sm text-gray-600">{agent.totalTickets} tickets • {agent.feedbackCount} feedback</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Avg Rating</p>
                      <p className="text-lg font-semibold">{agent.averageRating}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Satisfaction</p>
                      <p className="text-lg font-semibold">{agent.satisfactionRate}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Response Time</p>
                      <p className="text-lg font-semibold">{agent.responseTime}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Trend</p>
                      <p className={`text-lg font-semibold ${
                        agent.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {agent.trend}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown & Improvement Areas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="h-5 w-5" />
              Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              type="pie"
              data={analytics?.categoryBreakdown || []}
              xKey="category"
              yKey="count"
              height={300}
              colors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Improvement Areas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics?.improvementAreas.map((area, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={getPriorityColor(area.priority)}>
                      {area.priority}
                    </Badge>
                    <span className="font-medium">{area.area}</span>
                    <Badge className={getSentimentColor(area.sentiment)}>
                      <div className="flex items-center gap-1">
                        {getSentimentIcon(area.sentiment)}
                        {area.sentiment}
                      </div>
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{area.mentions}</p>
                    <p className="text-sm text-gray-600">mentions</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Recent Feedback
          </CardTitle>
          <div className="flex gap-4 mt-4">
            <div className="flex-1">
              <Input
                placeholder="Search feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select value={selectedAgent} onValueChange={setSelectedAgent}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {analytics?.agentPerformance.map((agent) => (
                  <SelectItem key={agent.id} value={agent.name}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {analytics?.categoryBreakdown.map((category) => (
                  <SelectItem key={category.category} value={category.category}>
                    {category.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredRecentFeedback.map((feedback) => (
              <div key={feedback.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{feedback.customer}</Badge>
                    <Badge variant="outline">{feedback.agent}</Badge>
                    <Badge className={getSentimentColor(feedback.sentiment)}>
                      <div className="flex items-center gap-1">
                        {getSentimentIcon(feedback.sentiment)}
                        {feedback.sentiment}
                      </div>
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {Array.from({ length: 5 }, (_, index) => (
                        <Star
                          key={index}
                          className={`h-4 w-4 ${
                            index < feedback.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(feedback.date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">{feedback.category}</Badge>
                </div>
                <p className="text-gray-700">{feedback.comment}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Industry Benchmarks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Industry Benchmarks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Average Rating</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Industry Average</span>
                  <span className="font-semibold">{analytics?.benchmarks.industryAverage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Competitor Average</span>
                  <span className="font-semibold">{analytics?.benchmarks.competitorAverage}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Our Average</span>
                  <span className="font-semibold text-green-600">{analytics?.benchmarks.ourAverage}</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Satisfaction Rate</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Industry Average</span>
                  <span className="font-semibold">{analytics?.benchmarks.industrySatisfaction}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Our Rate</span>
                  <span className="font-semibold text-green-600">{analytics?.benchmarks.ourSatisfaction}%</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Performance</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Rating vs Industry</span>
                  <span className="font-semibold text-green-600">+10.5%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Satisfaction vs Industry</span>
                  <span className="font-semibold text-green-600">+11.5%</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackAnalytics;
